import { useState, useEffect } from "react";
import { Drawer, IconButton, List } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowBack as ArrowBackIcon,
  DashboardCustomize as HomeIcon,
  SettingsSuggest as SettingsIcon,
  Logout
} from "@mui/icons-material";
import PaymentsIcon from "@mui/icons-material/Payments";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material/styles";
import classNames from "classnames";
import useStyles from "./styles";
import SidebarLink from "./components/SidebarLink/SidebarLink";
import logo from "../../assets/images/logo.svg";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
// import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import NotificationsIcon from "@mui/icons-material/Notifications";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";
import MailIcon from "@mui/icons-material/Mail";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import EmailIcon from "@mui/icons-material/Email";
import CampaignIcon from "@mui/icons-material/Campaign";

// --| using context files
import {
  useLayoutState,
  useLayoutDispatch,
  toggleSidebar,
  toggleSidebarOff,
  togglePermamence
} from "../../context/LayoutContext";
import {
  useUserDispatch,
  useUserState,
  logOut
} from "../../context/UserContext";

const Sidebar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { profile, appLogo } = useUserState();

  // global
  const { isSidebarOpened, isPermanent } = useLayoutState();
  const layoutDispatch = useLayoutDispatch();
  const userDispatch = useUserDispatch();

  const handleWindowWidthChange = () => {
    const windowWidth = window.innerWidth;
    const breakpointWidth = theme.breakpoints.values.md;
    const isSmallScreen = windowWidth < breakpointWidth;

    if (isSmallScreen && isPermanent) {
      togglePermamence(layoutDispatch);
    } else if (!isSmallScreen && !isPermanent) {
      togglePermamence(layoutDispatch);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowWidthChange);
    handleWindowWidthChange();

    return function cleanup() {
      window.removeEventListener("resize", handleWindowWidthChange);
    };
  });

  useEffect(() => {
    toggleSidebarOff(layoutDispatch);
  }, []);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeLink, setActiveLink] = useState(location.pathname);

  const handleToggleDropdown = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  const structure = [
    { id: 0, label: "Dashboard", link: "/dashboard", icon: <HomeIcon /> },
    // { id: 1, label: "Email Template", link: "/email-template", icon: <TrackChangesIcon /> },
    { id: 2, label: "User", link: "/user", icon: <CalendarTodayIcon /> },
    { id: 3, label: "Notificatiion", link: "/notification", icon: <NotificationsIcon /> },
    { id: 4, label: "Schools", link: "/schools", icon: <StoreMallDirectoryIcon /> },
    { id: 4, label: "Email Subcription", link: "/subscription", icon: <MailIcon /> },
    { id: 5, label: "Pricing", link: "/pricing", icon: <PaymentsIcon /> },
    { id: 6, label: "Partner", link: "/partner", icon: <PersonIcon /> },
    { id: 14, label: "Marketing", link: "/marketing", icon: <CampaignIcon /> },
    {
      id: 15,
      label: "Referrers",
      link: "/referrers",
      icon: <Icon icon="mdi:account-multiple-plus" width="24" />,
      children: [
        { label: "Unauthenticated", link: "/referrers/unauthenticated" },
        { label: "Authenticated", link: "/referrers/authenticated" }
      ]
    },
    { id: 7, label: "Email Management", link: "/email-management", icon: <EmailIcon /> },
    { id: 8, label: "Question Pool", link: "/question-pool", icon: <Icon icon="fluent:quiz-new-24-regular" width="24" /> },
    // { id: 6, label: "Reports", link: "/reports", icon: <ReceiptIcon /> },
    // { id: 7, label: "Leave Management", link: "/leavemgt", icon: <AdminLeaveIcon /> },
    // { id: 8, label: "Performance Evaluation", link: "/perfeval", icon: <PerformanceIcon /> },
    // { id: 9, label: "Label", link: "/resources", icon: <ResourcesIcon /> },
    // { id: 10, label: "Marketplace", link: "/marketplace", icon: <PluginIcon /> },
    { id: 11, label: "Settings", link: "/settings", icon: <SettingsIcon /> },
    {
      id: 12,
      label: "Reward",
      link: "/rewards",
      icon: <EmojiEventsIcon />,
      children: [
        { label: "Rewards", link: "/rewards" },
        { label: "Withdrawal", link: "/withdrawals" }
      ]
    },
    {
      id: 13,
      label: "Support",
      link: "/support/tickets",
      icon: <Icon icon="mdi:love" />
    }
  ];

  const Initial = profile?.first_name?.split("")[0].toUpperCase();

  return (
    <Drawer
      variant={isPermanent ? "permanent" : "temporary"}
      className={classNames(classes.drawer, {
        [classes.drawerOpen]: isSidebarOpened,
        [classes.drawerClose]: !isSidebarOpened
      })}
      classes={{
        paper: classNames({
          [classes.drawerOpen]: isSidebarOpened,
          [classes.drawerClose]: !isSidebarOpened
        })
      }}
      open={isSidebarOpened}
    >
      <div className={classes.sidebarLogo}>
        <Link to="/dashboard">
          <div className={classes.logoContent}>
            <img
              src={appLogo || logo}
              alt="App Logo"
              className={classes.logoContent}
            />
          </div>
        </Link>
      </div>
      <div className={classes.mobileBackButton}>
        <IconButton onClick={() => toggleSidebar(layoutDispatch)}>
          <ArrowBackIcon
            classes={{
              root: classNames(classes.headerIcon, classes.headerIconCollapse)
            }}
          />
        </IconButton>
      </div>
      <List className={classNames(classes.sidebarList, classes.mainSidebarList)}>
        {structure.map((link) => (
          <SidebarLink
            key={link.id}
            location={location}
            isSidebarOpened={isSidebarOpened}
            openDropdown={openDropdown}
            handleToggleDropdown={handleToggleDropdown}
            activeLink={activeLink}
            setActiveLink={setActiveLink}
            {...link}
          />
        ))}
      </List>

      <div className={classes.footer}>
        <Link className={classes.footerlink} to="/profile">
          <div className={classes.footercontainer}>
            <div className={classes.initials}>{Initial}</div>
            <div className={classes.footertextwrapper}>
              <p className={classes.name}>
                {`${profile?.first_name} ${profile?.last_name}`}
              </p>
              <p className={classes.email}>{profile?.email}</p>
            </div>
          </div>
        </Link>
        <div
          className={classes.logout}
          onClick={() => logOut(userDispatch, navigate)}
        >
          <Logout />
        </div>
      </div>
    </Drawer>
  );
};

export default Sidebar;
